// Backend-neutral plan coordinates: X/Z in mm; screen X/Y in CSS pixels.
const finite = (...values) => values.every(Number.isFinite);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function boundedViewportSize(cssWidth, cssHeight, dpr = 1) {
  if (!finite(cssWidth, cssHeight, dpr) || cssWidth < 0 || cssHeight < 0 || dpr <= 0) throw new RangeError('Invalid viewport size');
  const width = Math.min(16384, Math.max(1, Math.round(cssWidth)));
  const height = Math.min(16384, Math.max(1, Math.round(cssHeight)));
  let resolution = Math.min(dpr, 2, 4096 / width, 4096 / height, Math.sqrt(4_194_304 / (width * height)));
  // Pixi rounds physical axes independently; reserve one pixel per axis at the area cap.
  if (Math.round(width * resolution) * Math.round(height * resolution) > 4_194_304) resolution *= 0.999;
  return Object.freeze({ width, height, resolution, requestedDpr: dpr,
    pixelWidth: Math.round(width * resolution), pixelHeight: Math.round(height * resolution) });
}

export function createViewportCamera({ x = 0, z = 0, scale = 0.04, width = 1000, height = 600,
  minScale = 0.001, maxScale = 4, coordinateLimit = 1_000_000 } = {}) {
  if (!finite(x, z, scale, width, height, minScale, maxScale, coordinateLimit)
    || width <= 0 || height <= 0 || minScale <= 0 || maxScale < minScale || coordinateLimit <= 0) throw new RangeError('Invalid camera');
  let current = { x: clamp(x, -coordinateLimit, coordinateLimit), z: clamp(z, -coordinateLimit, coordinateLimit),
    scale: clamp(scale, minScale, maxScale), width, height };
  const set = (cx, cz, nextScale = current.scale) => {
    if (!finite(cx, cz, nextScale) || nextScale <= 0) throw new RangeError('Invalid camera update');
    current = { ...current, x: clamp(cx, -coordinateLimit, coordinateLimit), z: clamp(cz, -coordinateLimit, coordinateLimit), scale: clamp(nextScale, minScale, maxScale) };
  };
  const worldAt = (sx, sy) => ({ x: current.x + (sx - current.width / 2) / current.scale, z: current.z + (sy - current.height / 2) / current.scale });
  return Object.freeze({
    snapshot: () => Object.freeze({ ...current }),
    worldAt,
    screenAt: (wx, wz) => ({ x: (wx - current.x) * current.scale + current.width / 2, y: (wz - current.z) * current.scale + current.height / 2 }),
    set,
    resize(nextWidth, nextHeight) {
      if (!finite(nextWidth, nextHeight) || nextWidth <= 0 || nextHeight <= 0) throw new RangeError('Invalid camera extent');
      current = { ...current, width: nextWidth, height: nextHeight };
    },
    pan(dx, dy) { set(current.x - dx / current.scale, current.z - dy / current.scale); },
    zoomAt(factor, sx, sy) {
      if (!finite(factor, sx, sy) || factor <= 0) throw new RangeError('Invalid zoom');
      const anchor = worldAt(sx, sy), next = clamp(current.scale * factor, minScale, maxScale);
      set(anchor.x - (sx - current.width / 2) / next, anchor.z - (sy - current.height / 2) / next, next);
    },
    fit(bounds, padding = 32) {
      if (!bounds || !finite(bounds.minX, bounds.minZ, bounds.maxX, bounds.maxZ, padding)
        || bounds.maxX < bounds.minX || bounds.maxZ < bounds.minZ || padding < 0) throw new RangeError('Invalid fit bounds');
      set((bounds.minX + bounds.maxX) / 2, (bounds.minZ + bounds.maxZ) / 2,
        Math.min(Math.max(1, current.width - 2 * padding) / Math.max(1, bounds.maxX - bounds.minX),
          Math.max(1, current.height - 2 * padding) / Math.max(1, bounds.maxZ - bounds.minZ)));
    },
    bounds(padding = 0) {
      const a = worldAt(-padding, -padding), b = worldAt(current.width + padding, current.height + padding);
      return { minX: a.x, minZ: a.z, maxX: b.x, maxZ: b.z };
    }
  });
}

/** Two-pointer navigation; constant work per event, no model/geometry access. */
export function createViewportNavigation(camera, invalidate) {
  const pointers = new Map();
  const pair = () => {
    const [a, b] = [...pointers.values()];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, distance: Math.hypot(a.x - b.x, a.y - b.y) };
  };
  return {
    down(id, x, y) {
      if (!finite(x, y) || pointers.size >= 2 || pointers.has(id)) return false;
      pointers.set(id, { x, y }); return true;
    },
    move(id, x, y) {
      const old = pointers.get(id);
      if (!old || !finite(x, y)) return false;
      const before = pointers.size === 2 ? pair() : null;
      pointers.set(id, { x, y });
      if (before) {
        const after = pair();
        camera.pan(after.x - before.x, after.y - before.y);
        if (before.distance > 2 && after.distance > 2) camera.zoomAt(after.distance / before.distance, after.x, after.y);
      } else camera.pan(x - old.x, y - old.y);
      invalidate(); return true;
    },
    up(id) { pointers.delete(id); },
    cancel() { pointers.clear(); },
    wheel(delta, mode, x, y) {
      if (!finite(delta, x, y)) return;
      const pixels = delta * (mode === 1 ? 16 : mode === 2 ? camera.snapshot().height : 1);
      camera.zoomAt(Math.exp(-clamp(pixels, -500, 500) * 0.0015), x, y); invalidate();
    },
    get pointerCount() { return pointers.size; }
  };
}
